import {z} from 'zod';

import tabularDataToCsv from 'common/src/modules/shared/copilot/csvUtils/tabularDataToCsv';
import {excelApi} from 'common/src/modules/excel/excel.api';
import {
  CopilotExcelFileContent,
  ToolExecuteParams,
  ToolResult,
} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CampaignSetupAgentToolName} from '../../../campaignSetupAgent.enums';

const readValuesFromExcelToolSchema = z.object({
  fileId: z.string(),
  worksheetIndex: z.number(),
  columnNames: z.array(z.string().describe('The names of the columns to read')),
  rowsRanges: z
    .array(
      z.object({
        startLine: z.number().describe('The start line of the row range'),
        endLine: z.number().describe('The end line of the row range'),
      }),
    )
    .optional()
    .describe('The row ranges to read. If not provided, all rows will be read.'),
});

/**
 *
 * @returns A tool that reads the column values from an excel file
 */
export default function readValuesFromExcelTool() {
  async function execute({
    toolUtils,
    input: {fileId, worksheetIndex, columnNames, rowsRanges},
  }: ToolExecuteParams<typeof readValuesFromExcelToolSchema>): Promise<ToolResult> {
    const file = toolUtils.getFileById(fileId);

    if (!file) {
      return toolError('File not found');
    }

    const excelFile = file.content as CopilotExcelFileContent;

    if (!excelFile) {
      return toolError('File is not an excel file');
    }

    // read the column values from the excel file using the excel api
    const fileValues = await excelApi.readColumnValuesFromExcelFile({
      fileKey: file.fileKey,
      sheetName: excelFile.worksheets[worksheetIndex].name,
      columnsToReturn: columnNames,
      lineRanges: rowsRanges || [],
      returnDistinctRows: true,
    });

    const tabularData = fileValues.data.map((values) => {
      const row: Record<string, string> = {};

      fileValues.columns.forEach((column, columnIndex) => {
        row[column.name] = values[columnIndex];
      });

      return row;
    });

    return toolResult(tabularDataToCsv(tabularData));
  }

  return tool({
    name: CampaignSetupAgentToolName.ReadValuesFromExcel,
    description: 'Reads the column values from an excel file',
    execute,
    inputSchema: readValuesFromExcelToolSchema,
    displayName: 'Reading values from excel',
  });
}
